import { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Tabs, Tab } from "@mui/material";
import PropTypes from "prop-types";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AD_ACCOUNTS_TITLE,
  API_KEYS_TAB_TITLE,
  BRAND_SETUP_TITLE,
  SETTINGS_TITLE,
  USERS_TITLE,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import ConnectedAccounts from "./components/ConnectedAccounts";
import TitlePage from "../../components/Texts/TitlePage";
import { Helmet } from "react-helmet";
import { useSelectorApp, useDispatchApp } from "../../lib/redux";
import ContainerPage from "../../components/Containers/ContainerPage";
import { BrandSetup } from "./BrandSetup/BrandSetup";
import ModalSuccessBrandSetup from "../../components/Modal/ModalSuccessBrandSetup";
import { hasRoleCapability } from "../../utils/roleCapabilities";
import { getPathDatabaseByRole } from "../../actions/auth";
import useAgencyIsAppSumo from "../../hooks/useAgencyIsAppSumo";
import ApiKeys from "./components/ApiKeys";
import Users from "../Users/Users";
import {
  ADVERTISER_ROLE,
  ROUTE_INSIGHTS,
  SUPER_ADMIN_ROLE,
} from "../../utils/constants";
import { PENDING_CONTENT_APPROVAL_REVIEW_STORAGE_KEY } from "../../components/ContentApproval/contentApprovalUtils";
import { triggerClick } from "../../utils/triggerClick";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      className={"animate__animated animate__fadeIn animate__faster"}
      {...other}
      style={{ width: "100%" }}
    >
      {value === index && (
        <Box
          sx={{
            width: "100%",
            pt: 2,
            pb: 15,
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
    sx: {
      textTransform: "capitalize",
      "&.Mui-selected": {
        color: "white !important",
      },
    },
  };
}

const Settings = () => {
  const [value, setValue] = useState("brand-setup");
  const [isModalSuccessOpen, setIsModalSuccessOpen] = useState(false);

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const navigate = useNavigate();
  const { theme } = useSelectorApp((state) => state.ui);
  const [searchParams, setSearchParams] = useSearchParams();
  const { agencyID, role } = dispatch(getPathDatabaseByRole());
  const canAccessSettingsAccounts = hasRoleCapability(
    role,
    "canAccessSettingsAccounts"
  );
  const canAccessUsers =
    Boolean(agencyID) &&
    (role === ADVERTISER_ROLE || role === SUPER_ADMIN_ROLE);
  const { isAppSumo } = useAgencyIsAppSumo();

  const tabs = useMemo(
    () =>
      [
        {
          id: "brand-setup",
          label: t(BRAND_SETUP_TITLE),
          visible: true,
          content: <BrandSetup />,
        },
        {
          id: "accounts",
          label: t(AD_ACCOUNTS_TITLE),
          visible: canAccessSettingsAccounts,
          content: <ConnectedAccounts />,
        },
        {
          id: "users",
          label: t(USERS_TITLE),
          visible: canAccessUsers,
          content: <Users embedded />,
        },
        {
          id: "api-keys",
          label: t(API_KEYS_TAB_TITLE),
          visible: canAccessSettingsAccounts && isAppSumo,
          content: <ApiKeys />,
        },
      ].filter((tab) => tab.visible),
    [t, canAccessUsers, canAccessSettingsAccounts, isAppSumo]
  );

  const onChangeTab = (event, newValue) => {
    setValue(newValue);
  };

  const redirectUrlParams = useCallback(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "brand-insights") {
      navigate(ROUTE_INSIGHTS, { replace: true });
      return;
    }
    const openGenerateWithAI = searchParams.get("openGenerateWithAI");
    const openCalendar = searchParams.get("openCalendar");
    const openCompetitors = searchParams.get("openCompetitors");
    const openModalSuccess = searchParams.get("openModalSuccess");
    const openContentApprovalReview = searchParams.get(
      "openContentApprovalReview"
    );
    const contentID = searchParams.get("contentID");

    if (tabParam && tabs.some((tab) => tab.id === tabParam)) {
      setValue(tabParam);
    }

    if (openGenerateWithAI === "true") {
      triggerClick({ id: "button-open-gallery", delay: 100 });
      triggerClick({ id: "button-create-with-ia-modal-gallery", delay: 400 }); // 100 + 300
    }

    if (openContentApprovalReview === "true" && contentID) {
      sessionStorage.setItem(
        PENDING_CONTENT_APPROVAL_REVIEW_STORAGE_KEY,
        contentID
      );
      triggerClick({ id: "button-open-gallery", delay: 100 });
    }

    if (openCalendar === "true") {
      triggerClick({ id: "button-open-calendar", delay: 100 });
    }

    if (openCompetitors === "true") {
      triggerClick({ id: "button-open-competitor-scope", delay: 100 });
    }

    if (openModalSuccess === "true") {
      setIsModalSuccessOpen(true);
    }

    if (
      tabParam ||
      openGenerateWithAI ||
      openCalendar ||
      openCompetitors ||
      openModalSuccess ||
      openContentApprovalReview ||
      contentID
    ) {
      const newParams = new URLSearchParams(searchParams);
      [
        "tab",
        "openGenerateWithAI",
        "openCalendar",
        "openCompetitors",
        "openModalSuccess",
        "openContentApprovalReview",
        "contentID",
      ].forEach((param) => newParams.delete(param));
      setSearchParams(newParams, { replace: true });
    }
  }, [navigate, searchParams, setSearchParams, tabs]);

  useEffect(() => {
    redirectUrlParams();
  }, [redirectUrlParams]);

  return (
    <>
      <Helmet>
        <title>{`${theme.nameBusiness} - Settings`}</title>
      </Helmet>
      <ContainerPage hasMarginBottom={false} sx={{ width: "100%", px: 4.5 }}>
        <TitlePage
          sx={{
            mt: 5,
            mb: 1,
          }}
        >
          {t(SETTINGS_TITLE)}
        </TitlePage>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "#e1e1e1",
            position: "relative",
            width: "100%",
          }}
        >
          <Tabs
            value={value}
            onChange={onChangeTab}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "background.paperContrastText !important",
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={tab.label}
                {...a11yProps(tab.id)}
                sx={{ color: "background.paperContrastText" }}
              />
            ))}
          </Tabs>
        </Box>
        {tabs.map((tab) => (
          <CustomTabPanel key={tab.id} value={value} index={tab.id}>
            {tab.content}
          </CustomTabPanel>
        ))}
      </ContainerPage>

      {isModalSuccessOpen && (
        <ModalSuccessBrandSetup
          onCloseModal={() => setIsModalSuccessOpen(false)}
        />
      )}
    </>
  );
};

export default Settings;
